import httpClient from "./httpClient";
import {
  getApiData,
  getApiErrorCode,
  type ApiError,
  type ApiResponse,
  type DocumentLibraryParam,
  type DocumentLibraryReference,
  type Pagination,
} from "../types/api";

function isLibraryReference(value: unknown): value is DocumentLibraryReference {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as DocumentLibraryReference).parentSlug === "string" &&
    typeof (value as DocumentLibraryReference).entrySlug === "string"
  );
}

export default class DocumentLibraryApi {
  static getLibrary(
    library: DocumentLibraryParam,
    params: Record<string, unknown> = {},
    languageKey: string | null = null
  ): Promise<ApiResponse<{ pagination?: Pagination }>> {
    const requestParams: Record<string, unknown> = { ...params };
    const libraryPath = this.resolveLibraryPath(library);

    if (languageKey) {
      requestParams.lang = languageKey;
    }

    return httpClient
      .get(API_URL + "/document_library/" + libraryPath, {
        params: requestParams,
      })
      .then((response) => response.data);
  }

  static resolveLibraryPath(library: DocumentLibraryParam): string {
    if (isLibraryReference(library)) {
      return library.parentSlug + "/" + library.entrySlug;
    }

    return library;
  }

  static isAvailable(
    library: DocumentLibraryParam,
    languageKey: string | null = null
  ): Promise<boolean> {
    return this.getLibrary(
      library,
      {
        page: 1,
        pagesize: 1,
      },
      languageKey
    )
      .then((response) => {
        const pagination = getApiData(response, {})?.pagination || {};

        return Boolean((pagination.total || 0) > 0 || (pagination.result || []).length > 0);
      })
      .catch((error: ApiError) => {
        if (getApiErrorCode(error) === "DOCUMENT_LIBRARY_NOT_FOUND") {
          return false;
        }

        throw error;
      });
  }

  static getFile(documentId: number | string, languageKey: string | null): Promise<ApiResponse> {
    return httpClient
      .get(API_URL + "/documents/" + documentId + "/file", {
        params: {
          lang: languageKey,
        },
      })
      .then((response) => response.data);
  }

  static getFileStreamUrl(
    documentId: number | string,
    languageKey: string | null,
    download = false
  ): string {
    const params = new URLSearchParams();

    if (languageKey) {
      params.set("lang", languageKey);
    }

    if (download) {
      params.set("download", "1");
    }

    const query = params.toString();
    return API_URL + "/documents/" + documentId + "/stream" + (query ? "?" + query : "");
  }

  static search(
    search: string,
    languageKey: string | null = null,
    multilingual = false
  ): Promise<ApiResponse> {
    const params: Record<string, unknown> = {
      search,
    };

    if (languageKey) {
      params.lang = languageKey;
    }

    if (multilingual) {
      params.multilingual = 1;
    }

    return httpClient
      .get(API_URL + "/documents/search", {
        params,
      })
      .then((response) => response.data);
  }
}
